/**
 * @fileoverview Name — or change — who teaches a class.
 *
 * Assignment is its own decision made at its own time. A term is built before
 * it is staffed, and a teacher who leaves mid-term has to be replaced without
 * rebuilding the class, so this is reachable from the class row and the class
 * page rather than living only inside the create form.
 *
 * Two things the copy has to be honest about:
 *
 * * **The schedule travels with the class.** The meetings are keyed by
 *   instructor, so assigning moves them. The replacement inherits the term
 *   rather than being handed a class with an empty calendar.
 * * **What was already taught does not.** A lesson records who stood in the
 *   room. Reattributing it would rewrite history to match the present, and the
 *   rollups and the pay that follow read that history.
 *
 * The candidates are the ORG's instructors, not only the program's — the same
 * set the create form offers, for the same reason: an org's instructors are
 * not automatically on any one program, so a list drawn from program
 * membership is empty on a new program and refuses every name it could have
 * offered. Naming someone the org employs joins them to the program.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button } from "@/components/studio/primitives";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

export default function AssignClassInstructorDialog({
  open,
  onOpenChange,
  klass,
  instructors = [],
  onAssigned,
}) {
  const [picked, setPicked] = useState("");
  const [saving, setSaving] = useState(false);
  const [orgInstructors, setOrgInstructors] = useState([]);

  useEffect(() => {
    if (open) setPicked(klass?.instructor_id || "");
  }, [open, klass]);

  /* Failing quietly is right: the program's own members are already in hand,
     and losing the wider list must not take the dialog down with it. */
  const loadOrgInstructors = useCallback(async () => {
    if (!open) return;
    try {
      const res = await orgApi.get("/org/instructors");
      setOrgInstructors(res.data?.instructors || []);
    } catch {
      setOrgInstructors([]);
    }
  }, [open]);

  useEffect(() => { loadOrgInstructors(); }, [loadOrgInstructors]);

  /** Program members first — they are the likely answer. */
  const choices = useMemo(() => {
    const memberIds = new Set(instructors.map((i) => i.user_id));
    const rows = instructors.map((i) => ({ user_id: i.user_id, name: i.name, onProgram: true }));
    for (const i of orgInstructors) {
      if (memberIds.has(i.user_id)) continue;
      rows.push({ user_id: i.user_id, name: i.name || i.email, onProgram: false });
    }
    return rows;
  }, [instructors, orgInstructors]);

  const chosen = choices.find((c) => c.user_id === picked);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await orgApi.put(`/org/classes/${klass.class_id}/instructor`, {
        instructor_id: picked || null,
      });
      const moved = res.data?.slots_moved || 0;
      toast.success(
        picked
          ? `${klass.name} assigned${moved ? ` — ${moved} scheduled meeting${moved === 1 ? "" : "s"} moved too` : ""}`
          : `${klass.name} has nobody on it now — its schedule is paused, not lost`,
      );
      onAssigned?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't assign that class"));
    } finally {
      setSaving(false);
    }
  };

  if (!klass) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">
            Who teaches {klass.name}?
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} data-testid="assign-class-form">
          {choices.length === 0 ? (
            <p data-testid="assign-no-instructors" className="font-sans text-[15px] text-[var(--text-tertiary)]">
              Nobody has joined your organization yet. Invite an instructor and name this class on the
              invitation — it&rsquo;s theirs the moment they accept.
            </p>
          ) : (
            <>
              <select
                value={picked}
                onChange={(e) => setPicked(e.target.value)}
                aria-label="Head instructor"
                data-testid="assign-instructor"
                className={inputClass}
              >
                <option value="">Nobody yet</option>
                {choices.map((i) => (
                  <option key={i.user_id} value={i.user_id}>
                    {i.onProgram ? i.name : `${i.name} — not on this program yet`}
                  </option>
                ))}
              </select>
              {chosen && !chosen.onProgram && (
                <p data-testid="assign-will-join" className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                  {chosen.name} will be added to this program.
                </p>
              )}
            </>
          )}

          {klass.instructor_id && picked && picked !== klass.instructor_id && (
            <p data-testid="assign-handover-note" className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]">
              The class keeps its schedule — the new instructor picks up the term where it is. Sessions already
              taught stay credited to {klass.instructor_name}.
            </p>
          )}
          {klass.instructor_id && !picked && (
            <p data-testid="assign-clear-note" className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]">
              The schedule is kept but paused — nobody&rsquo;s calendar or reminders will show it. Whoever takes
              the class over inherits the same meetings.
            </p>
          )}

          <div className="mt-5 flex items-center gap-3">
            <Button type="submit" disabled={saving || choices.length === 0} data-testid="assign-submit">
              {saving ? "Saving…" : "Save"}
            </Button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
            >
              Cancel
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
